import React, { useState, useEffect } from 'react';
import LoginScreen from './LoginScreen';
import BetaPasswordModal from './BetaPasswordModal';
import WaitlistSection from './WaitlistSection';

const LandingPage = ({ onLogin, onShowDriverOnboarding }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [showBetaPassword, setShowBetaPassword] = useState(false);
  const [loginType, setLoginType] = useState('rider');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [resetToken, setResetToken] = useState(null);

  // Detect reset token in URL on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('resetToken');
    if (token) {
      setResetToken(token);
      setLoginType('rider');
      setShowLogin(true);
    }
  }, []);

  // WhatsApp number for contact
  const WHATSAPP_NUMBER = '27670187407'; // South Africa format without +
  
  // Pre-built WhatsApp message for ride booking
  const getWhatsAppUrl = (message) => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
  };

  // Book a ride - redirect to WhatsApp
  const handleBookRideClick = () => {
    const message = `Hi CompanionCab! 👋

I'd like to book a ride. Here are my details:

📍 Pickup Address: 
📍 Destination: 
📅 Date: 
⏰ Time: 
📞 Phone: 
👤 Name: 

Special Requirements:
☐ Elderly Assistance
☐ Medical Appointment
☐ Shopping Assistance

Additional Notes:`;
    window.open(getWhatsAppUrl(message), '_blank');
  };

  // Check if user has beta access
  const hasBetaAccess = () => {
    return sessionStorage.getItem('betaAccess') === 'true';
  };

  // Handle login button clicks - show beta password modal first
  const handleProtectedLogin = (type) => {
    if (hasBetaAccess()) {
      setLoginType(type);
      setShowLogin(true);
    } else {
      setLoginType(type);
      setShowBetaPassword(true);
    }
  };

  // Called after successful beta password entry
  const handleBetaSuccess = (type) => {
    setShowBetaPassword(false);
    setLoginType(type);
    setShowLogin(true);
  };

  // General contact via WhatsApp
  const handleWhatsAppContact = () => {
    const message = `Hi CompanionCab! 👋 I'd like to learn more about your services.`;
    window.open(getWhatsAppUrl(message), '_blank');
  };

  // Become a driver - show driver onboarding
  const handleBecomeDriver = () => {
    if (onShowDriverOnboarding) {
      onShowDriverOnboarding();
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What makes Companion Cab different from regular taxi services?",
      answer: "Our drivers are specially trained companions who provide assistance throughout your journey. We focus on elderly passengers, offering patient, caring service that goes beyond just transportation."
    },
    {
      question: "How much does a typical ride cost?",
      answer: "Our transparent pricing is R10.50 per kilometer plus R2.00 per minute for companion time. There are no hidden fees, and round-trip service is included. You'll receive a clear quote before booking."
    },
    {
      question: "Can companions assist with shopping or errands?",
      answer: "Absolutely! Our companions can help with shopping, carrying items, and providing assistance with daily errands. This service is included in your companion time rate."
    },
    {
      question: "How do I book a ride?",
      answer: "Currently, you can book a ride by contacting us directly via WhatsApp. Simply click 'Book a Ride' and send us your trip details. Our team will get back to you promptly with a quote and confirmation."
    },
    {
      question: "Are your drivers background checked?",
      answer: "Yes, all our companion drivers undergo thorough background checks, first-aid training, and specialized companion training to ensure the highest standards of care and safety."
    },
    {
      question: "When will the app be available?",
      answer: "We're currently in beta testing! Join our waitlist to be notified when the app launches. Early waitlist members will receive exclusive discounts and priority booking access."
    }
  ];

  return (
    <div className="landing-page-modern">
      {/* Navigation Bar */}
      <nav className="modern-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_pwa-vercel-prod/artifacts/z056vcil_Logo.png.png" 
              alt="Companion Cab" 
              className="nav-logo-icon"
            />
            <span className="logo-text">Companion Cab</span>
          </div>
          <div className="nav-links">
            <button className="nav-whatsapp-btn" onClick={handleWhatsAppContact}>
              <span>💬</span> Contact Us
            </button>
            <button className="nav-signin-btn" onClick={() => handleProtectedLogin('rider')}>
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="modern-hero">
        <div className="floating-elements">
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="coming-soon-badge">
              <span>🚀</span> App Coming Soon
            </div>
            <h1 className="hero-title">
              Transportation with
              <br />
              <span className="gradient-text">Caring Companions</span>
            </h1>
            <p className="hero-subtitle">
              Professional companion transportation services for elderly and mobility-assisted passengers. 
              Contact us via WhatsApp to book your ride today!
            </p>
            <button className="btn-whatsapp-primary hero-cta-btn" onClick={handleBookRideClick}>
              <span>💬</span> Book Your Ride Now
            </button>
          </div>
          
          <div className="hero-visual">
            <div className="orbit-ring"></div>
            <div className="round-trip-service">
              <div className="service-title">Round Trip Service</div>
              
              <div className="service-legend">
                <div className="legend-item-simple">
                  <span style={{color: '#10b981', fontSize: '18px'}}>●</span>
                  <span>Home Pickup</span>
                </div>
                <div className="legend-item-simple">
                  <span style={{color: '#dc2626', fontSize: '18px'}}>●</span>
                  <span>Destination</span>
                </div>
                <div className="legend-item-simple">
                  <span style={{color: '#ec4899', fontSize: '18px'}}>●</span>
                  <span>Companion Vehicle</span>
                </div>
              </div>
              
              <div className="trip-journey">
                <div className="location-point home-point">🏠</div>
                <div className="journey-line"></div>
                <div className="companion-car">🚗</div>
                <div className="location-point destination-point">🎯</div>
              </div>
              
              <div className="service-description">
                <h4>Accompanied Services</h4>
                <p>Doctor appointments • Shopping trips • Errands • Medical facilities • Pharmacy visits • Social outings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dual CTA Section */}
      <section className="dual-cta-section">
        <div className="dual-cta-container">
          <div className="passenger-cta">
            <div className="cta-content">
              <h3>Need a Ride?</h3>
              <p>Book safe, reliable transportation with trained companions who understand your needs.</p>
              <button className="btn-whatsapp-primary" onClick={handleBookRideClick}>
                <span>💬</span> Book via WhatsApp
                <div className="btn-shine"></div>
              </button>
              <p className="cta-note">Round trip service • 24hr advance booking • Subject to driver availability</p>
            </div>
          </div>
          
          <div className="driver-cta">
            <div className="cta-content">
              <h3>Ready to Make a Difference?</h3>
              <p>Join our team of compassionate drivers and help elderly and mobility-assisted passengers in your community.</p>
              <button 
                className="btn-whatsapp-secondary"
                onClick={handleBecomeDriver}
              >
                <span>🚗</span> Apply to Drive
                <div className="btn-shine"></div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <WaitlistSection />

      {/* Services Section */}
      <section id="services" className="modern-services">
        <div className="services-container">
          <div className="section-header">
            <h2>Our Companion Services</h2>
            <p>Specialized care for every journey</p>
          </div>
          <div className="services-grid-modern">
            <div className="service-card-modern">
              <div className="service-icon-modern">👴</div>
              <h3>Elderly Care</h3>
              <p>Patient, caring companions who understand the needs of senior passengers</p>
              <div className="service-features">
                <span>✓ Patient assistance</span>
                <span>✓ Medical awareness</span>
                <span>✓ Comfort focus</span>
              </div>
            </div>
            <div className="service-card-modern">
              <div className="service-icon-modern">🏥</div>
              <h3>Medical Appointments</h3>
              <p>Safe transport to and from medical appointments with companion assistance</p>
              <div className="service-features">
                <span>✓ On-time guarantee</span>
                <span>✓ Waiting service</span>
                <span>✓ Document help</span>
              </div>
            </div>
            <div className="service-card-modern">
              <div className="service-icon-modern">🛒</div>
              <h3>Shopping Assistance</h3>
              <p>Companions can assist with shopping and errands during your trip</p>
              <div className="service-features">
                <span>✓ Carrying help</span>
                <span>✓ Store navigation</span>
                <span>✓ Item selection</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="modern-pricing">
        <div className="pricing-container">
          <div className="section-header">
            <h2>Transparent Pricing</h2>
            <p>No hidden fees, just honest rates</p>
          </div>
          <div className="pricing-card-modern">
            <div className="pricing-header">
              <h3>Simple & Fair Pricing</h3>
            </div>
            <div className="pricing-items">
              <div className="pricing-row">
                <div className="price-label">
                  <span className="price-icon">📍</span>
                  <span>Distance Rate</span>
                </div>
                <div className="price-value">R10.50 per km</div>
              </div>
              <div className="pricing-row">
                <div className="price-label">
                  <span className="price-icon">⏰</span>
                  <span>Companion Time</span>
                </div>
                <div className="price-value">R2.00 per minute</div>
              </div>
            </div>
            <div className="pricing-benefits">
              <div className="benefit">✓ Round trip service included</div>
              <div className="benefit">✓ No hidden fees or surcharges</div>
              <div className="benefit">✓ Companion assistance throughout journey</div>
              <div className="benefit">✓ Transparent billing after each trip</div>
            </div>
            <button className="pricing-cta-btn" onClick={handleBookRideClick}>
              <span>💬</span> Get a Quote via WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="modern-how-it-works">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Simple steps to caring transportation</p>
          </div>
          <div className="steps-grid-modern">
            <div className="step-modern">
              <div className="step-icon">
                <div className="step-number">1</div>
              </div>
              <div className="step-content">
                <h3>Contact Us</h3>
                <p>Send us a WhatsApp message with your trip details and special requirements</p>
              </div>
            </div>
            <div className="step-modern">
              <div className="step-icon">
                <div className="step-number">2</div>
              </div>
              <div className="step-content">
                <h3>Get Your Quote</h3>
                <p>We'll confirm your booking and match you with a companion driver</p>
              </div>
            </div>
            <div className="step-modern">
              <div className="step-icon">
                <div className="step-number">3</div>
              </div>
              <div className="step-content">
                <h3>Enjoy Your Journey</h3>
                <p>Travel safely with your caring companion who assists throughout the trip</p>
              </div>
            </div>
            <div className="step-modern">
              <div className="step-icon">
                <div className="step-number">4</div>
              </div>
              <div className="step-content">
                <h3>Simple Payment</h3>
                <p>Pay conveniently after trip completion with transparent pricing breakdown</p>
              </div>
            </div>
          </div>
          <div className="steps-cta">
            <button className="btn-whatsapp-primary large" onClick={handleBookRideClick}>
              <span>💬</span> Start Your Booking
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="modern-faq">
        <div className="faq-container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about our service</p>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(index)}>
                  <h3>{faq.question}</h3>
                  <div className={`faq-toggle ${expandedFaq === index ? 'rotate' : ''}`}>+</div>
                </div>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="modern-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>CompanionCab</h3>
              <p>Professional companion transportation services for those who need caring assistance.</p>
              <div className="footer-contact-info">
                <a href="mailto:infocompcab@gmail.com" className="footer-contact-link">
                  <span>📧</span> infocompcab@gmail.com
                </a>
                <a href={getWhatsAppUrl('Hi CompanionCab!')} target="_blank" rel="noopener noreferrer" className="footer-contact-link whatsapp">
                  <span>💬</span> WhatsApp Us
                </a>
              </div>
            </div>
            <div className="footer-section">
              <h4>Services</h4>
              <ul>
                <li>Elderly Care Rides</li>
                <li>Medical Appointments</li>
                <li>Shopping Assistance</li>
                <li>Errand Companion</li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Quick Actions</h4>
              <ul>
                <li>
                  <button onClick={handleBookRideClick} className="footer-link">
                    <span>💬</span> Book a Ride
                  </button>
                </li>
                <li>
                  <button onClick={handleBecomeDriver} className="footer-link">
                    <span>🚗</span> Become a Driver
                  </button>
                </li>
                <li>
                  <a href="#waitlist" className="footer-link">
                    <span>📧</span> Join Waitlist
                  </a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Staff Access</h4>
              <p className="footer-staff-note">Beta password required</p>
              <ul>
                <li>
                  <button onClick={() => handleProtectedLogin('driver')} className="footer-link staff-link">
                    <span>🔐</span> Driver Login
                  </button>
                </li>
                <li>
                  <button onClick={() => handleProtectedLogin('admin')} className="footer-link staff-link">
                    <span>🔐</span> Admin Login
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 CompanionCab. All rights reserved.</p>
            <p className="footer-tagline">Caring transportation for those who matter most.</p>
          </div>
        </div>
      </footer>

      {/* Beta Password Modal */}
      {showBetaPassword && (
        <BetaPasswordModal
          onSuccess={handleBetaSuccess}
          onClose={() => setShowBetaPassword(false)}
          targetLoginType={loginType}
        />
      )}

      {/* Login Modal */}
      {showLogin && (
        <LoginScreen
          onClose={() => { setShowLogin(false); setResetToken(null); }}
          onLogin={onLogin}
          initialUserType={loginType}
          resetToken={resetToken}
          initialMode={resetToken ? 'reset' : 'login'}
        />
      )}
    </div>
  );
};

export default LandingPage;
